import { useCallback } from 'react';
import { useToast } from '../../core/providers/ToastProvider';
import { useTranslation } from 'react-i18next';

/**
 * Hook personnalisé pour afficher des notifications toast avec traduction intégrée
 * @param {string} namespace - Le namespace de traduction (par défaut: 'common')
 * @returns {Object} - Fonctions pour afficher différents types de notifications
 */
export const useNotification = (namespace = 'common') => {
  const { showToast } = useToast();
  const { t } = useTranslation(namespace);

  /**
   * Affiche une notification de type success
   * @param {string} key - Clé de traduction ou message direct
   * @param {Object} options - Options de traduction (si clé de traduction)
   * @param {number} duration - Durée d'affichage en ms (défaut: 3000)
   */
  const success = useCallback((key, options = {}, duration = 3000) => {
    const message = key.startsWith('toast.') || key.includes(':') ? t(key, options) : key;
    showToast(message, 'success', duration);
  }, [showToast, t]);

  /**
   * Affiche une notification de type error
   * @param {string} key - Clé de traduction ou message direct
   * @param {Object} options - Options de traduction (si clé de traduction)
   * @param {number} duration - Durée d'affichage en ms (défaut: 4000)
   */
  const error = useCallback((key, options = {}, duration = 4000) => {
    const message = key.startsWith('toast.') || key.includes(':') ? t(key, options) : key;
    showToast(message, 'error', duration);
  }, [showToast, t]);

  /**
   * Affiche une notification de type info
   * @param {string} key - Clé de traduction ou message direct
   * @param {Object} options - Options de traduction (si clé de traduction)
   * @param {number} duration - Durée d'affichage en ms (défaut: 3000)
   */
  const info = useCallback((key, options = {}, duration = 3000) => {
    const message = key.startsWith('toast.') || key.includes(':') ? t(key, options) : key;
    showToast(message, 'info', duration);
  }, [showToast, t]);

  return {
    success,
    error,
    info
  };
}; 